import classNames from 'classnames';
import { Body1, Checkbox, TableCell, TableCellLayout, TableRow, type TableCellProps } from '@fluentui/react-components';
import { ControlledTooltip } from '@shared/components/ControlledTooltip';
import { DateLabel } from '@shared/components/dates/DateLabel';
import ArchiveTableAction from '@shared/components/tables/ArchiveTableAction';
import DragHandle from '@shared/components/tables/DragHandle';
import { SmartTableCell } from '@shared/components/tables/SmartTableCell';
import { useReorderableRow } from '@shared/hooks/useReorderableRow.hook';
import AvatarGroup from '@modules/users/components/AvatarGroup';
import StatusBadge from '@modules/items/components/ItemsTable/StatusBadge';
import type { TItem } from '@modules/items/types/items.types';
import { useEmbeddedEditMode } from '@modules/documents';
import { TITLE_PLACEHOLDER } from '@modules/documents/tiptap/nodes/title/constants';
import { CHANGE_TEMPLATE_ASSESSMENT_DRAG_TYPE } from '../constants';
import { getChangeTemplatePath } from '../paths';
import type { TChangeTemplateAssessment } from '../types';

type TProps = {
  changeTemplateId: string;
  assessment: TChangeTemplateAssessment;
  number: number;
  getCellProps: (column: string) => TableCellProps;
  canChange: boolean;
  requiredPending: boolean;
  requiredDisabledReason: string;
  reorderDisabledReason: string;
  onReorder: (sourceId: string, targetId: string) => void;
  onRequiredChange: (assessment: TChangeTemplateAssessment, required: boolean) => void;
  onDelete: (assessment: TChangeTemplateAssessment) => void;
  document?: TItem;
  templateVersion?: string;
};

const ChangeTemplateAssessmentRow = ({
  changeTemplateId,
  assessment,
  number,
  getCellProps,
  canChange,
  requiredPending,
  requiredDisabledReason,
  reorderDisabledReason,
  onReorder,
  onRequiredChange,
  onDelete,
  document,
  templateVersion,
}: TProps) => {
  const { rowRef, handleRef, isDragging, isOver } = useReorderableRow({
    type: CHANGE_TEMPLATE_ASSESSMENT_DRAG_TYPE,
    id: assessment._id,
    scope: changeTemplateId,
    canReorder: canChange && !reorderDisabledReason,
    onReorder,
  });
  const { isEditMode } = useEmbeddedEditMode();
  const href = getChangeTemplatePath(changeTemplateId, {
    assessmentId: assessment._id,
    templateVersion,
    autoEdit: isEditMode,
  });
  // A completed template version shows the completed document version it pins, not the document as it is now.
  const pinned = 'completedDate' in assessment ? assessment : undefined;
  const modifiedDate = pinned ? pinned.completedDate : document?.stats.modifiedDate;
  const authors = pinned ? pinned.authors : document?.stats.authors;
  return (
    <TableRow ref={rowRef} className={classNames({ 'opacity-40': isDragging, '!bg-neutralBg-3': isOver })}>
      <TableCell {...getCellProps('number')}>
        <div className="flex items-center gap-2 text-neutralFg-3">
          {canChange && <DragHandle ref={handleRef} disabledReason={reorderDisabledReason} />}
          <Body1>{number}</Body1>
        </div>
      </TableCell>
      <SmartTableCell
        cellProps={getCellProps('title')}
        // Like the document's empty title line.
        variant={{ kind: 'link', href, label: assessment.title || TITLE_PLACEHOLDER }}
        openMode="sameTab"
        tooltip={{ threshold: 70 }}
        after={canChange && <ArchiveTableAction label="Remove" onArchive={() => onDelete(assessment)} />}
      />
      <TableCell {...getCellProps('required')}>
        <ControlledTooltip
          relationship="inaccessible"
          enable={!!requiredDisabledReason}
          content={requiredDisabledReason}
          withArrow
        >
          <Checkbox
            checked={assessment.required}
            disabled={!canChange || requiredPending}
            onChange={(_, { checked }) => onRequiredChange(assessment, checked === true)}
          />
        </ControlledTooltip>
      </TableCell>
      <TableCell {...getCellProps('modified')}>
        {modifiedDate ? <DateLabel short>{modifiedDate}</DateLabel> : '-'}
      </TableCell>
      <TableCell {...getCellProps('status')}>
        {document ? <StatusBadge item={pinned ? { ...document, hasDraft: false } : document} /> : '-'}
      </TableCell>
      <TableCell {...getCellProps('authors')}>
        <TableCellLayout>{authors ? <AvatarGroup ids={authors} /> : '-'}</TableCellLayout>
      </TableCell>
    </TableRow>
  );
};

export default ChangeTemplateAssessmentRow;
